import { XStack, YStack } from 'tamagui';
import PSIcon from './ui/PSIcon';
import PSText from './ui/PSText';
import { UserDetailsLine } from './UserDetails';
import { openURL } from '@/src/utils/linkingUtils';
import PSLinkButton from './ui/PSLinkButton';
import PSBox from './ui/PSBox';

/**
 * CheckoutUserDetails — the "Your details" box on the payment confirmation
 * screen (molecule).
 *
 * A section with header "Your details" containing a bordered box that lists
 * the booking contact: name, email, mobile. Each is an icon + value pair,
 * stacked.
 *
 * Render-only, and the values are GUARANTEED present: the confirm screen is only
 * reachable logged in, and a logged-in user always has all three (a session is
 * issued only once mobile + email + name are complete — see the auth flow). So
 * there's no null/empty handling here; the screen passes finished strings.
 *
 * `socialLink` is the one line that's NOT contact info — it's what an
 * approval-only tier collected via `SocialLinkSheet` (see `eventCart`'s
 * `socialLink`), undefined on every other booking. It's the one line editable
 * from here (`onEditSocialLink` reopens the same sheet); the other three are
 * the session's own facts and aren't fixable on this screen.
 */
export default function CheckoutUserDetails({
  name,
  email,
  mobile,
  socialLink,
  onEditSocialLink,
  headerLabel = 'Your details',
}: {
  name: string;
  email: string;
  mobile: string;
  socialLink?: string;
  onEditSocialLink?: () => void;
  /** Header text for the section — defaults to "Your details". */
  headerLabel?: string;
}) {
  return (
    <YStack gap="$md">
      <PSText.H1 color="$textDark">{headerLabel}</PSText.H1>
      <PSBox gap={'$space.md'}>
        {name ? (
          <UserDetailsLine value={name} iconName="person-outline" />
        ) : null}
        {email ? (
          <UserDetailsLine value={email} iconName="mail-outline" />
        ) : null}
        {mobile ? (
          <UserDetailsLine value={mobile} iconName="call-outline" />
        ) : null}
        {socialLink ? (
          <XStack gap="$space.sm" items="center">
            <PSIcon name="link-outline" />
            <PSText.Body2
              textDecorationLine="underline"
              numberOfLines={1}
              flex={1}
              onPress={() => openURL(socialLink)}
            >
              {socialLink}
            </PSText.Body2>
            {onEditSocialLink ? (
              <PSLinkButton title="Edit" onPress={onEditSocialLink} />
            ) : null}
          </XStack>
        ) : null}
      </PSBox>
    </YStack>
  );
}
